import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { usePrefetch } from "../../utils/hooks/usePrefetch";

// MUI
import Button from "@mui/material/Button";

// Components
import Header from "./components/Header"
import Card from "./components/Card";
import Finished from "./components/Finished";

// Utils
import * as urls from "../../utils/urls";

// Styles
import styles from "./index.module.css";

// Types
import { KeyDate, Question, QUESTION_TYPES } from "../../types";

// Utils
import ThemeProvider from "../../utils/theme";

interface QuestionCardsProps {
  id: string;
  type: 'quiz' | 'practice';
  questions: any[];
  duration: number;
  user: any;
  backPath: string;
  title: string;
  fromLevel: boolean;
  quizContext?: 'regular' | 'game';
}

const MemoizedHeader = React.memo(Header);
const MemoizedCard = React.memo(Card);

const QuestionCards: React.FC<QuestionCardsProps> = ({ id, type, questions, duration, user, backPath, title, fromLevel, quizContext = 'regular' }) => {
  const { t } = useTranslation();
  const urlBackPath = new URLSearchParams(window.location.search).get('back_path');

  // State
  const [localQuestions, setLocalQuestions] = useState<Question[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [startTime, setStartTime] = useState(Date.now()); // Set immediately
  const [timeLeft, setTimeLeft] = useState<number | null>(duration);
  const [lives, setLives] = useState(0);
  const [keyDate, setKeyDate] = useState<KeyDate | null>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);

  // Flags
  const [finished, setFinished] = useState<boolean>(false);

  // Refs
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeLeftRef = useRef<NodeJS.Timeout | null>(null);

  // Methods
  const loadQuestions = useCallback(() => {
    if (questions.length === 0) return;

    if (type === 'quiz') {
      setLocalQuestions(questions);
    }
  }, [questions, type]);

  // Effects
  useEffect(() => {
    loadQuestions();
    setLives(Math.floor(Math.random() * 5) + 1);
    setSessionId(crypto.randomUUID());
  }, []);

  useEffect(() => {
    if (duration && duration > 0) {
      setTimeLeft(duration);
      
      timeLeftRef.current = setInterval(() => {
        setTimeLeft((prev) => (prev !== null && prev > 0 ? prev - 1 : 0));
      }, 1000);
    } else {
      setTimeLeft(null);
    }

    return () => {
      if (timeLeftRef.current) clearInterval(timeLeftRef.current);
    }
  }, [duration]);

  useEffect(() => {
    if (timeLeft !== null && timeLeft <= 0) handleFinishQuiz();
  }, [timeLeft]);

  // Handlers
  const handleQuit = async () => {
    if (urlBackPath || backPath) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeLeftRef.current) clearInterval(timeLeftRef.current);

      Turbo.visit(urlBackPath || backPath);
    }
  }

  const handleComplete = async () => {
    // TODO: Implement complete logic
    if (urlBackPath || backPath) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeLeftRef.current) clearInterval(timeLeftRef.current);

      Turbo.visit(urlBackPath || backPath);
    }
  }

  const handleNextQuestion = () => {
    if (questionIndex < localQuestions.length - 1) {
      setSelectedAnswers([]);
      setQuestionIndex(questionIndex + 1);
    } else {
      handleFinishQuiz();
    }
  }

  const handleAnswerQuestion = useCallback(async (id: string | number, answer: any) => {
    const questionData = localQuestions.find(question => question.id === id);
    if (!questionData) return;
    
    const questionType = questionData.type;

    if (
      questionType &&
      questionType !== QUESTION_TYPES.MATCHING_PAIRS &&
      questionType !== QUESTION_TYPES.FILL_IN_THE_BLANK
    ) {
      const numberOfCorrectAnswers = questionData.answers.filter((ans) => ans.is_correct).length;
      let hasMoreMultipleCorrectAnswers = false;
      
      setSelectedAnswers(prev => {
        const updated = [...prev, answer.id];
        if (answer.is_correct && updated.length < numberOfCorrectAnswers) {
          hasMoreMultipleCorrectAnswers = true;
        }
      
        return updated;
      });
      
      if (hasMoreMultipleCorrectAnswers) return;
    }
    
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      },
      body: JSON.stringify({
        question_result: {
          answer: answer.answer,
          correct: answer.is_correct,
          question_id: questionData.id,
        },
        session_id: sessionId
      }),
    };
    await fetch(urls.questionResultUrl(), options);

    let userAnswer;
    switch (questionData.type) {
      case 'matching_pairs':
      case 'fill_in_the_blank':
        userAnswer = answer.answer;
        break;
      default:
        userAnswer = answer.id;
        break;
    }

    const updatedQuestion = { 
      ...questionData, 
      user_answer: userAnswer,
      is_user_answer_correct: answer.is_correct,
      answer_id: answer.id
    };
    setLocalQuestions(localQuestions.map(question => question.id === id ? updatedQuestion : question));
  }, [localQuestions, questionIndex, sessionId]);

  const handleFinishQuiz = useCallback(() => {
    // Clear the interval
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeLeftRef.current) clearInterval(timeLeftRef.current);

    // TODO: Save the answers to the server
    // TODO: Redirect to the next page

    // TODO: Show the result
    // TODO: Show the score
    // TODO: Show the correct answers
    // TODO: Show the wrong answers
    // TODO: Show the skipped questions

    setFinished(true);
    setKeyDate({
      id: crypto.randomUUID(),
      name: 'Key Date',
      date: dayjs().add(Math.floor(Math.random() * 10) + 1, 'day').format('YYYY-MM-DD'),
      old_progress: 10,
      progress: 50,
    });
  }, [localQuestions, questionIndex, selectedAnswers]);

  const handleShuffleQuestions = useCallback(() => {
    const shuffled = [...localQuestions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    setLocalQuestions(shuffled);
    setQuestionIndex(0);
    setSelectedAnswers([]);
  }, [localQuestions]);

  const handleRegenerateQuestions = useCallback(async () => {
    try {
      const response = await fetch(urls.quizRegenerateUrl(id), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });

      if (response.ok) {
        Turbo.visit(urls.quizUrl(id));
      } else {
        const error = await response.json();
        alert(t('questionCards.errors.regeneration_failed'));
      }
    } catch (error) {
      alert(t('questionCards.errors.regeneration_failed'));
    }
  }, [id, t]);

  const prefetch = usePrefetch();

  const resultsPath = useMemo(() => {
    if (!sessionId) return null;
    return `/quizzes/${id}/results?session_id=${sessionId}&start_time=${startTime}`;
  }, [id, sessionId, startTime]);

  const shouldRedirectToResults = useMemo(() => {
    return fromLevel || quizContext === 'game';
  }, [fromLevel, quizContext]);

  const isQuestionAnswered = useMemo(() => {
    const questionData = localQuestions[questionIndex];
    if (!questionData) return false;
    return questionData.user_answer !== null && questionData.user_answer !== undefined;
  }, [localQuestions, questionIndex]);

  const isLastQuestion = useMemo(() => {
    return questionIndex === localQuestions.length - 1;
  }, [localQuestions, questionIndex]);

  if (finished) {
    if (shouldRedirectToResults) {
      if (resultsPath) {
        window.Turbo.visit(resultsPath, { action: 'replace' });
      }
      return null;
    }

    return (
      <div className='w-full bg-transparent rounded-lg flex flex-col items-center'>
        <Finished
          localQuestions={localQuestions}
          startTime={startTime}
          user={user}
          keyDate={keyDate}
          onBack={handleQuit}
          onComplete={handleComplete}
          type={type}
          title={title}
          quizId={id}
          sessionId={sessionId}
        />
      </div>
    )
  }

  return (
    <ThemeProvider>
      <div className={`${styles.mainContainer} w-full ${fromLevel? 'bg-white border-stone-300 border rounded-3xl shadow-lg dark:bg-stone-650' : 'gap-1'}`}>

        <MemoizedHeader
          totalQuestions={localQuestions.length}
          currentQuestion={questionIndex + 1}
          timeLeft={timeLeft}
          type={type}
          lives={lives}
          onQuit={handleQuit}
          onShuffle={handleShuffleQuestions}
          onRegenerate={handleRegenerateQuestions}
          title={title}
          fromLevel={fromLevel}
        />

        <MemoizedCard question={localQuestions[questionIndex]} onAnswerQuestion={handleAnswerQuestion} selectedAnswers={selectedAnswers} fromLevel={fromLevel}/>

        <div className={styles.footer}>
          <Button
            className={`${styles.nextButton} ${fromLevel ? '!w-full light:!bg-violet-400 light:disabled:!bg-gray-100' : '!w-[80%]'}`}
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleNextQuestion}
            disabled={!isQuestionAnswered}
            onMouseEnter={() => isLastQuestion && fromLevel && resultsPath && prefetch(resultsPath, 'mouseenter')}
            onTouchStart={() => isLastQuestion && fromLevel && resultsPath && prefetch(resultsPath, 'touchstart')}
          >
            {isLastQuestion ? t('actions.finish') : t('actions.next')}
          </Button>
        </div>
      </div>
    </ThemeProvider>
  )
}

export default QuestionCards;
